import type { FloorPlan } from '../api/types.js';

const EPSILON = 1e-6;

// Per-axis check: the projection uses independent sx/sy, so a calibration
// with the two world points sharing an X (or Z) value silently collapses
// one axis to scale 0 even though the total Euclidean distance is non-zero.
// We require all four per-axis deltas to be > EPSILON, which is also the
// operator-side convention for a usable 2-point calibration.
export function isCalibrationDegenerate(cal: FloorPlan['calibration']): boolean {
  const dwX = Math.abs(cal.world2[0] - cal.world1[0]);
  const dwY = Math.abs(cal.world2[1] - cal.world1[1]);
  const dpX = Math.abs(cal.pixel2[0] - cal.pixel1[0]);
  const dpY = Math.abs(cal.pixel2[1] - cal.pixel1[1]);
  return dwX < EPSILON || dwY < EPSILON || dpX < EPSILON || dpY < EPSILON;
}

// Axis-aligned per-axis affine mapping (MVP contract).
// With 2 points we cannot solve a general 6-parameter affine; we model
// the mapping as TWO independent 1D affines (one per axis).
//
// MVP CONTRACT: this is correct ONLY when world XZ axes and floor plan
// pixel XY axes are aligned (independent scale + sign per axis, no
// rotation). The post-MVP studio editor will provide 3+ point calibration
// for rotated plans. Per-axis sign handles X/Y mirroring naturally.
export function worldToPixel(
  worldXZ: readonly [number, number],
  cal: FloorPlan['calibration'],
): [number, number] {
  const dwX = cal.world2[0] - cal.world1[0];
  const dwY = cal.world2[1] - cal.world1[1];
  const dpX = cal.pixel2[0] - cal.pixel1[0];
  const dpY = cal.pixel2[1] - cal.pixel1[1];
  const sx = Math.abs(dwX) > EPSILON ? dpX / dwX : 0;
  const sy = Math.abs(dwY) > EPSILON ? dpY / dwY : 0;
  return [
    cal.pixel1[0] + (worldXZ[0] - cal.world1[0]) * sx,
    cal.pixel1[1] + (worldXZ[1] - cal.world1[1]) * sy,
  ];
}

export function pixelToWorld(
  pixelXY: readonly [number, number],
  cal: FloorPlan['calibration'],
): [number, number] {
  const dwX = cal.world2[0] - cal.world1[0];
  const dwY = cal.world2[1] - cal.world1[1];
  const dpX = cal.pixel2[0] - cal.pixel1[0];
  const dpY = cal.pixel2[1] - cal.pixel1[1];
  const isx = Math.abs(dpX) > EPSILON ? dwX / dpX : 0;
  const isy = Math.abs(dpY) > EPSILON ? dwY / dpY : 0;
  return [
    cal.world1[0] + (pixelXY[0] - cal.pixel1[0]) * isx,
    cal.world1[1] + (pixelXY[1] - cal.pixel1[1]) * isy,
  ];
}

// Camera heading: XZ-plane angle from +X axis, atan2(dz, dx). Range [0, 360).
// We use camera.lookAt - camera.position projected to XZ — Y component is
// dropped so a tilted camera (looking down) still reports a valid heading.
// pos === lookAt → return 0 (degenerate, e.g. orbit not initialized).
export function cameraHeadingDeg(
  pos: readonly [number, number, number],
  lookAt: readonly [number, number, number],
): number {
  const dx = lookAt[0] - pos[0];
  const dz = lookAt[2] - pos[2];
  if (Math.abs(dx) < EPSILON && Math.abs(dz) < EPSILON) return 0;
  const rad = Math.atan2(dz, dx);
  const deg = (rad * 180) / Math.PI;
  return (deg + 360) % 360;
}
